import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Table, Button, Input, Spin, message, Dropdown, Menu, Popover } from "antd";
import moment from "moment";
import CryptoJS from "crypto-js";
import { PhoneOutlined, DownOutlined, FilterOutlined } from "@ant-design/icons";
import Select from "react-select";
import Swal from "sweetalert2";
import exportService from "../../pages/hms/utils/exportService";
import UserService from "../../pages/hms/utils/userService";
import { renderDate } from "../../pages/hms/utils/dateFormatter";
import PrintReportPreview from "./reports/PrintReportPreview";
import PartnerService from "./utils/partnerService";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";

const GuestCardView = () => {
  const { Search } = Input;
  const [searchText, setSearchText] = useState("");
  const [guestData, setGuestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [pdfVisible, setPdfVisible] = useState(false);
  const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
  const business_date = localStorage.getItem("business_date") || moment().format("YYYY-MM-DD");
  const [dateRange, setDateRange] = useState(null); // Null for no date filter by default
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);
  const [columnSelectionVisible, setColumnSelectionVisible] = useState(false);
  const { getFullname } = UserService();
  const { getPartnerName } = PartnerService();
  const secretKey = "abde100199199111";

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((err) => {
          console.error("Failed to enter fullscreen:", err);
        });
        setFullScreen(true);
      } else {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen:", err);
        });
        setFullScreen(false);
      }
    };

    const handleFullscreenChange = () => {
      setFullScreen(!!document.fullscreenElement);
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener("click", handleClick);
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener("click", handleClick);
      }
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => {
          console.error("Failed to exit fullscreen during cleanup:", err);
        });
      }
    };
  }, []);

  useEffect(() => {
    fetchGuests();
  }, [dateRange, statusFilter, sourceFilter]);

  const fetchGuests = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      let url = `${apiURL}/booking/reservations?inhouse=true`;
      if (dateRange) {
        const startDate = dateRange.startDate.format("YYYY-MM-DD");
        const endDate = dateRange.endDate.format("YYYY-MM-DD");
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch in-house guests.");
      }

      const data = await response.json();
      if (data.success) {
        setGuestData(
          data.reservations.map((guest) => ({
            ...guest,
            num_adults: guest.num_adults || 0,
            num_children: guest.num_children || 0,
          }))
        );
      } else {
        throw new Error("No in-house guests found.");
      }
    } catch (err) {
      setError(err.message);
      Swal.fire({
        title: "Error!",
        text: err.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = guestData.filter((guest) => {
    const safeToLowerCase = (value) =>
      typeof value === "string" && value ? value.toLowerCase() : "";

    const searchLower = safeToLowerCase(searchText);

    const matchesSearch =
      safeToLowerCase(guest.booking_number).includes(searchLower) ||
      safeToLowerCase(guest.guest_name).includes(searchLower) ||
      safeToLowerCase(guest.guest_email).includes(searchLower) ||
      safeToLowerCase(guest.room_numbers).includes(searchLower);

    const matchesStatus = !statusFilter || guest.booking_status === statusFilter.value;
    const matchesSource = !sourceFilter || guest.booking_source === sourceFilter.value;

    return matchesSearch && matchesStatus && matchesSource;
  });

  const handleDateChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (!prevRange) return null; // No date range to shift
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
        } else {
          return prevRange;
        }
      } else if (param === null) {
        // Clear date filter
        return null;
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (newRange) {
        if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
          message.error("Invalid date selected.");
          return prevRange;
        }
        if (newRange.startDate.isAfter(newRange.endDate)) {
          message.error("Start date cannot be after end date.");
          return prevRange;
        }
        if (newRange.startDate.isAfter(moment())) {
          message.error("Check-in dates cannot be in the future.");
          return prevRange;
        }
      }

      return newRange;
    });
  };

  const handleFilterApply = () => {
    // Bootstrap modal will close via data-bs-dismiss
  };

  const handleFilterReset = () => {
    setStatusFilter(null);
    setSourceFilter(null);
    setSearchText("");
    setDateRange(null); // Reset date filter
  };

  const statusOptions = [
    { value: "checked_in", label: "Checked In" },
  ];

  const sourceOptions = [
    { value: "Direct", label: "Direct" },
    { value: "Online", label: "Online" },
    { value: "Agency", label: "Agency" },
  ];

  const totalAdults = filteredData.reduce((sum, guest) => sum + (guest.num_adults || 0), 0);
  const totalChildren = filteredData.reduce((sum, guest) => sum + (guest.num_children || 0), 0);
  const totalNights = filteredData.reduce(
    (sum, guest) =>
      sum + moment(guest.check_out_date).diff(moment(guest.check_in_date), "days"),
    0
  );

  const exportHeaders = [
    { key: "guest_name", title: "Guest Name" },
    { key: "guest_phone", title: "Guest Phone" },
    { key: "booking_number", title: "Guest Reg No" },
    { key: "booking_status", title: "Status" },
    { key: "booking_source", title: "Booking Source" },
    { key: "room_numbers", title: "Room No" },
    { key: "room_types", title: "Room Type" },
    { key: "meal_plan", title: "Meal Plan" },
    { key: "check_in_date", title: "Check-In" },
    { key: "check_out_date", title: "Check-Out" },
    { key: "nights", title: "Night" },
    { key: "guests", title: "Adults/Children" },
    { key: "guest_email", title: "Guest Email" },
    { key: "guest_nationality", title: "Guest Nationality" },
    { key: "gender", title: "Gender" },
    { key: "created_at", title: "Reservation Date" },
    { key: "created_by", title: "Created By" },
    { key: "checked_in_by", title: "Checked-In By" },
    { key: "updated_by", title: "Updated By" },
  ];

  const exportData = filteredData.map((guest) => ({
    guest_name: guest.guest_name,
    guest_phone: guest.guest_phone || "N/A",
    booking_number: guest.booking_number,
    booking_status: guest.booking_status.replace("_", " ").toUpperCase(),
    booking_source: getPartnerName(guest.booking_source) || "N/A",
    room_numbers: guest.room_numbers || "N/A",
    room_types: guest.room_types || "N/A",
    meal_plan: guest.meal_plan || "N/A",
    check_in_date: moment(guest.check_in_date).format("DD MMM YYYY"),
    check_out_date: moment(guest.check_out_date).format("DD MMM YYYY"),
    nights: moment(guest.check_out_date).diff(moment(guest.check_in_date), "days") || 0,
    guests: `${guest.num_adults || 0} / ${guest.num_children || 0}`,
    guest_email: guest.guest_email,
    guest_nationality: guest.guest_nationality || "N/A",
    gender: guest.gender || "N/A",
    created_at: moment(guest.created_at).format("YYYY-MM-DD"),
    created_by: getFullname(guest.created_by) || "N/A",
    checked_in_by: getFullname(guest.checked_in_by) || "N/A",
    updated_by: getFullname(guest.updated_by) || "N/A",
  }));

  const totalsForPageFooter = [
    { key: "Total Adults", value: totalAdults },
    { key: "Total Children", value: totalChildren },
    { key: "Total Nights", value: totalNights },
  ];

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData,
      exportHeaders,
      `inhouse_guest_report${suffix}.xlsx`,
      "In House Guest Report"
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData,
      exportHeaders,
      `inhouse_guest_report${suffix}.csv`
    );
  };

  const handleExportPDF = () => {
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      "In House Guest List",
      `inhouse_guest_report${suffix}.pdf`,
      false,
      true,
      totalsForPageFooter
    );
  };

  const handlePrint = () => {
    setPdfVisible(true);
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>
      <Menu.Item key="pdf" onClick={handleExportPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      render: (name, record) => (
        <Link
          to={`/hospitality/reservation-details/${encodeURIComponent(
            CryptoJS.AES.encrypt(record.booking_id.toString(), secretKey).toString()
          )}`}
          className="colored-box guest-name-box"
          style={{ textAlign: "left" }}
        >
          {name}
        </Link>
      ),
      sorter: (a, b) => a.guest_name.localeCompare(b.guest_name),
      width: 200,
      fixed: "left",
    },
    {
      title: "Guest Phone",
      dataIndex: "guest_phone",
      render: (phone) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          <PhoneOutlined /> {phone || "N/A"}
        </span>
      ),
      width: 150,
      fixed: "left",
    },
    {
      title: "Guest Reg No",
      dataIndex: "booking_number",
      render: (booking_number, record) => {
        try {
          const encryptedId = CryptoJS.AES.encrypt(
            record.booking_id.toString(),
            secretKey
          ).toString();
          return (
            <Link
              to={`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`}
              className="colored-box reservation-no-box"
            >
              {booking_number}
            </Link>
          );
        } catch (error) {
          console.error("Encryption Error:", error);
          return <span className="colored-box reservation-no-box">Error</span>;
        }
      },
      sorter: (a, b) => a.booking_number.localeCompare(b.booking_number),
      width: 200,
      fixed: "left",
    },
    {
      title: "Booking Source",
      dataIndex: "booking_source",
      render: (source) => (
        <span className="colored-box source-box">{getPartnerName(source) || "N/A"}</span>
      ),
      width: 150,
    },
    {
      title: "Room No",
      dataIndex: "room_numbers",
      render: (room_numbers) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {room_numbers || "N/A"}
        </span>
      ),
      width: 120,
    },
    {
      title: "Room Type",
      dataIndex: "room_types",
      render: (room_types) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {room_types || "N/A"}
        </span>
      ),
      width: 150,
    },
    {
      title: "Meal Plan",
      dataIndex: "meal_plan",
      render: (meal_plan) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {meal_plan || "N/A"}
        </span>
      ),
      width: 120,
    },
    {
      title: "Check-In",
      dataIndex: "check_in_date",
      render: (date) => (
        <span className="date-box check-in-box">{renderDate(date)}</span>
      ),
      sorter: (a, b) => new Date(a.check_in_date) - new Date(b.check_in_date),
      width: 150,
    },
    {
      title: "Check-Out",
      dataIndex: "check_out_date",
      render: (date) => (
        <span className="date-box check-out-box">{renderDate(date)}</span>
      ),
      sorter: (a, b) => new Date(a.check_out_date) - new Date(b.check_out_date),
      width: 150,
    },
    {
      title: "Night",
      dataIndex: "nights",
      render: (nights, record) => {
        const checkIn = moment(record.check_in_date);
        const checkOut = moment(record.check_out_date);
        const nightCount = checkOut.diff(checkIn, "days");
        return <span className="date-box night-box">{nightCount || 0}</span>;
      },
      sorter: (a, b) => {
        const nightsA = moment(a.check_out_date).diff(moment(a.check_in_date), "days");
        const nightsB = moment(b.check_out_date).diff(moment(b.check_in_date), "days");
        return nightsA - nightsB;
      },
      width: 110,
    },
    {
      title: "Adults/Children",
      render: (_, record) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {`${record.num_adults || 0} / ${record.num_children || 0}`}
        </span>
      ),
      width: 120,
    },
    {
      title: "Guest Email",
      dataIndex: "guest_email",
      render: (email) => (
        <a href={`mailto:${email}`} className="colored-box email-box">{email}</a>
      ),
      width: 250,
    },
    {
      title: "Guest Nationality",
      dataIndex: "guest_nationality",
      render: (nationality) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {nationality || "N/A"}
        </span>
      ),
      width: 120,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      render: (gender) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {gender || "N/A"}
        </span>
      ),
      width: 100,
    },
    {
      title: "Reservation Date",
      dataIndex: "created_at",
      render: (date) => (
        renderDate(date)
      ),
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
      width: 170,
    },
    {
      title: "Created By",
      dataIndex: "created_by",
      render: (userId) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {getFullname(userId) || "N/A"}
        </span>
      ),
      width: 150,
    },
    {
      title: "Checked-In By",
      dataIndex: "checked_in_by",
      render: (userId) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {getFullname(userId) || "N/A"}
        </span>
      ),
      width: 150,
    },
    {
      title: "Updated By",
      dataIndex: "updated_by",
      render: (userId) => (
        <span className="colored-box phone-box" style={{ textAlign: "left" }}>
          {getFullname(userId) || "N/A"}
        </span>
      ),
      width: 150,
    },
  ];

  const getDynamicHeight = () => {
    return window.innerHeight - 150;
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-2">
              <h3 className="page-title">In House Guests</h3>
            </div>
            <div className="col-md-10">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: "220px" }}>
<DatePicker
  initialSettings={{
    singleDatePicker: true, // Enable single date selection
    showDropdowns: true,
    maxDate: moment(business_date).toDate(), // Restrict to business date
    startDate: moment(business_date).toDate(),
    ranges: {
      "Today": [moment(business_date).toDate(), moment(business_date).toDate()],
      "Yesterday": [
        moment(business_date).subtract(1, "day").toDate(),
        moment(business_date).subtract(1, "day").toDate(),
      ],
      "This Week": [
        moment(business_date).startOf("week").toDate(),
        moment(business_date).toDate(),
      ],
      "This Month": [
        moment(business_date).startOf("month").toDate(),
        moment(business_date).toDate(),
      ],
    },
    locale: {
      format: "DD MMM YYYY",
      applyLabel: "Select",
      cancelLabel: "Cancel",
    },
  }}
  onCallback={(start) => {
    // For single date, set both start and end to the same date
    handleDateChange({ start, end: start });
  }}
>
  <div className="input-group">
    <input
      type="text"
      className="form-control"
      value={
        dateRange
          ? dateRange.startDate.isSame(dateRange.endDate)
            ? dateRange.startDate.format("DD MMM YYYY")
            : `${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`
          : "Filter By Check-In Date"
      }
      style={{
        width: "220px",
        height: "38px",
        fontSize: "16px",
        fontWeight: "bold",
        textAlign: "center",
        padding: "0 10px 0 10px",
        borderRight: "none",
        backgroundColor: "#ffffff",
      }}
      readOnly
    />
    <div className="input-group-append">
      <span
        className="input-group-text"
        style={{
          height: "38px",
          backgroundColor: "#fff",
          borderLeft: "none",
          borderRadius: "0 4px 4px 0",
          cursor: "pointer",
        }}
      >
        <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
      </span>
    </div>
  </div>
</DatePicker>
{dateRange && (
  <Button
    type="text"
    icon={<i className="fas fa-times" />}
    onClick={() => handleDateChange(null)}
    style={{ marginLeft: "8px" }}
    title="Remove Date Filter"
  />
)}
                </div>
                <div style={{ flexGrow: 1, maxWidth: 400 }}>
                  <Search
                    placeholder="Search by Name, Email, or Booking ID"
                    allowClear
                    enterButton="Search"
                    size="large"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center flex-shrink-0 gap-2">
                  <div className="view-icons d-flex align-items-center gap-1">
                    <Link
                      to="#"
                      className="btn btn-link"
                      data-bs-toggle="modal"
                      data-bs-target="#scrollable-modal"
                    >
                      <i className="las la-filter" />
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-link"
                      id="collapse-header"
                      ref={maximizeBtnRef}
                    >
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                    <Dropdown
                      overlay={downloadMenu}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <Button
                        className="custom-btn-drop d-inline-flex align-items-center"
                        type="text"
                      >
                        <i className="la la-download custom-icon me-2" /> Download{" "}
                        <DownOutlined className="ms-2" />
                      </Button>
                    </Dropdown>
                  </div>
                  <div
                    className="stats-box d-flex align-items-center justify-content-center gap-4 px-4 py-2 rounded shadow-sm"
                    style={{
                      minWidth: "320px",
                      width: "fit-content",
                      background: "#f8f9fa",
                      border: "2px solid #ddd",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    <span className="d-flex align-items-center" style={{ minWidth: "140px" }}>
                      <i className="fas fa-users text-primary me-2" style={{ fontSize: "18px" }}></i>
                      Adults: {totalAdults}
                    </span>
                    <span className="d-flex align-items-center" style={{ minWidth: "140px" }}>
                      <i className="fas fa-child text-warning me-2" style={{ fontSize: "18px" }}></i>
                      Children: {totalChildren}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PrintReportPreview
          visible={pdfVisible}
          onClose={() => setPdfVisible(false)}
          reportTitle="In-House Guest"
          propertyName="In-House Guest"
          data={exportData}
          headers={exportHeaders}
          footerData={totalsForPageFooter}
          totalColumns={["guests", "nights"]}
          onDownloadPDF={handleExportPDF}
          apiEndpoint="transactions/generate-pdf"
          apiUrl={process.env.REACT_APP_API_URL}
        />

        <div
          className="table-responsive"
          style={{
            minHeight: "60vh",
            height: "calc(100vh - 150px)",
            overflow: "auto",
          }}
        >
          {loading ? (
            <div
              id="loader-wrapper-"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : error ? (
            <p
              className="text-danger text-center"
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {error}
            </p>
          ) : filteredData.length === 0 ? (
            <div
              className="no-data-container text-center mt-4"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                alt="No data available"
                style={{ width: "150px", marginBottom: "20px" }}
              />
              <h3>No Data Available</h3>
              <p>
                No in-house guests found
                {dateRange ? " for the selected check-in date range" : ""}.
              </p>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredData}
              pagination={{ pageSize: 20 }}
              className="table-striped custom-table"
              rowKey={(record) => record.booking_id}
              scroll={{
                x: 2000,
                y: getDynamicHeight(),
              }}
            />
          )}
        </div>

        <div
          className="modal custom-modal fade"
          id="scrollable-modal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="filterModalTitle"
          aria-hidden="true"
        >
          <div
            className="modal-dialog modal-lg"
            style={{
              margin: 0,
              position: "fixed",
              right: 0,
              height: "100vh",
              width: "40%",
              zIndex: 1060,
            }}
            role="document"
          >
            <div className="modal-content h-100">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalTitle">
                  Filter
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
              >
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div
                      className="form-group d-flex align-items-center"
                      style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}
                    >
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>
                        Status:
                      </label>
                      <Select
                        options={statusOptions}
                        placeholder="Select Status"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "300px",
                            borderRadius: 4,
                            borderColor: "#d9d9d9",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div
                      className="form-group d-flex align-items-center"
                      style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}
                    >
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>
                        Booking Source:
                      </label>
                      <Select
                        options={sourceOptions}
                        placeholder="Select Source"
                        value={sourceFilter}
                        onChange={setSourceFilter}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "300px",
                            borderRadius: 4,
                            borderColor: "#d9d9d9",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleFilterReset}
                  data-bs-dismiss="modal"
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFilterApply}
                  data-bs-dismiss="modal"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestCardView;